import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getHealthInsurancePayouts, recalculateHealthInsuranceMonth, markHealthInsurancePayoutPaid, getHealthInsuranceMonthlySummary, getHealthInsuranceList } from '../../model/api';
import Table from './common/Table';
import LoadingSpinner from './common/LoadingSpinner';

const HealthInsurancePayouts = () => {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [payouts, setPayouts] = useState([]);
    const [calculating, setCalculating] = useState(false);

    // Policy View Modal State
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [policyData, setPolicyData] = useState({ loading: false, data: [] });
    const [selectedPartnerName, setSelectedPartnerName] = useState('');

    // Payment Modal State
    const [showPayModal, setShowPayModal] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [paymentData, setPaymentData] = useState({
        transaction_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Bank Transfer',
        payment_notes: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [summaryRes, payoutsRes] = await Promise.all([
                getHealthInsuranceMonthlySummary(month),
                getHealthInsurancePayouts({ month })
            ]);

            if (summaryRes.ok) setSummary(summaryRes.data);
            if (payoutsRes.ok) setPayouts(payoutsRes.data || []);

        } catch (error) {
            console.error('Error fetching payouts:', error);
            toast.error('Failed to load payout data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [month]);

    const handleRecalculate = async () => {
        if (!window.confirm(`Recalculate payouts for ${month}? This will update unpaid records.`)) return;

        setCalculating(true);
        try {
            const result = await recalculateHealthInsuranceMonth(month);
            if (result.ok) {
                toast.success(result.message);
                fetchData();
            } else {
                toast.error(result.error || 'Recalculation failed');
            }
        } catch (error) {
            console.error('Recalculate error:', error);
            toast.error('Failed to recalculate');
        } finally {
            setCalculating(false);
        }
    };

    const handleMarkPaid = (payout) => {
        setSelectedPayout(payout);
        setPaymentData({
            transaction_id: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_method: 'Bank Transfer',
            payment_notes: ''
        });
        setShowPayModal(true);
    };

    const submitPayment = async (e) => {
        e.preventDefault();
        try {
            const result = await markHealthInsurancePayoutPaid(selectedPayout._id, paymentData);
            if (result.ok) {
                toast.success('Marked as Paid');
                setShowPayModal(false);
                fetchData();
            } else {
                toast.error(result.error || 'Failed to update');
            }
        } catch (error) {
            console.error('Payment error:', error);
            toast.error('Error updating payment');
        }
    };

    const handleViewPolicies = async (payout) => {
        setSelectedPartnerName(payout.user_id?.name);
        setShowPolicyModal(true);
        setPolicyData({ loading: true, data: [] });

        try {
            // Fetch policies for this partner and month
            const params = {
                partner_id: payout.user_id?._id || payout.user_id,
                month: month
            };
            const result = await getHealthInsuranceList(params);

            if (result.ok) {
                setPolicyData({ loading: false, data: result.data || [] });
            } else {
                setPolicyData({ loading: false, data: [] });
                toast.error('Failed to load policies');
            }
        } catch (error) {
            console.error('Fetch policies error:', error);
            setPolicyData({ loading: false, data: [] });
            toast.error('Error loading policies');
        }
    };

    const columns = [
        { header: 'Partner', accessor: (row) => row.user_id?.name || 'Unknown' },
        { header: 'Role', accessor: (row) => row.user_id?.role || 'N/A' },
        { header: 'Commission', accessor: (row) => `₹${row.total_commission?.toLocaleString('en-IN')}` },
        {
            header: 'Policies', accessor: (row) => (
                <button
                    onClick={() => handleViewPolicies(row)}
                    className="text-indigo-600 hover:text-indigo-900 text-sm underline"
                >
                    View Policies
                </button>
            )
        },
        { header: 'Payout Amount', accessor: (row) => `₹${row.payout_amount?.toLocaleString('en-IN')}` },
        {
            header: 'Status', accessor: (row) => (
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {row.status.toUpperCase()}
                </span>
            )
        },
        {
            header: 'Transaction', accessor: (row) => row.status === 'paid' ? (
                <div>
                    <div className="font-medium">{row.transaction_id}</div>
                    <div className="text-xs text-gray-500">{new Date(row.payment_date || row.paid_at).toLocaleDateString('en-IN')}</div>
                </div>
            ) : '-'
        },
        {
            header: 'Actions', accessor: (row) => (
                row.status === 'unpaid' && (
                    <button
                        onClick={() => handleMarkPaid(row)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                        Mark Paid
                    </button>
                )
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <label className="font-medium text-gray-700">Month:</label>
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="border rounded px-3 py-2"
                    />
                </div>
                <button
                    onClick={handleRecalculate}
                    disabled={calculating}
                    className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                    {calculating ? 'Calculating...' : 'Recalculate Month'}
                </button>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
                        <div className="text-gray-500 text-sm">Total Policies</div>
                        <div className="text-2xl font-bold">{summary.totalPolicies}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
                        <div className="text-gray-500 text-sm">Total Premium</div>
                        <div className="text-2xl font-bold">₹{summary.totalPremium?.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
                        <div className="text-gray-500 text-sm">Total Commission</div>
                        <div className="text-2xl font-bold">₹{summary.totalCommission?.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-indigo-500">
                        <div className="text-gray-500 text-sm">Total Paid</div>
                        <div className="text-2xl font-bold">₹{summary.totalPaid?.toLocaleString('en-IN')}</div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800">Payouts List</h3>
                </div>
                {loading ? <div className="p-8"><LoadingSpinner /></div> : <Table columns={columns} data={payouts} />}
            </div>

            {/* Policy View Modal */}
            {showPolicyModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Policies for {selectedPartnerName} ({month})</h3>
                            <button onClick={() => setShowPolicyModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                        </div>

                        {policyData.loading ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy No</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Premium</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Percent</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {policyData.data.length > 0 ? (
                                            policyData.data.map((policy) => (
                                                <tr key={policy._id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{policy.policy_number}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{policy.customer_name}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{policy.premium_amount?.toLocaleString('en-IN')}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{policy.commission_percentage}%</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">₹{policy.commission_amount?.toLocaleString('en-IN')}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(policy.createdAt).toLocaleDateString()}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No policies found for this period.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="mt-6 flex justify-end">
                            <button onClick={() => setShowPolicyModal(false)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPayModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Mark Payout as Paid</h3>
                        <div className="mb-4 text-sm text-gray-600">
                            Partner: <span className="font-medium text-gray-900">{selectedPayout?.user_id?.name}</span><br />
                            Amount: <span className="font-medium text-gray-900">₹{selectedPayout?.payout_amount?.toLocaleString('en-IN')}</span>
                        </div>
                        <form onSubmit={submitPayment} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Payment Date *</label>
                                <input
                                    type="date"
                                    required
                                    value={paymentData.payment_date}
                                    onChange={e => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Transaction ID / UTR *</label>
                                <input
                                    required
                                    value={paymentData.transaction_id}
                                    onChange={e => setPaymentData({ ...paymentData, transaction_id: e.target.value })}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                    placeholder="e.g. UPI12345678"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                                <select
                                    value={paymentData.payment_method}
                                    onChange={e => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                >
                                    <option value="Bank Transfer">Bank Transfer</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                    value={paymentData.payment_notes}
                                    onChange={e => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                    rows="2"
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setShowPayModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Confirm Payment</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthInsurancePayouts;
